import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import apiDefinitions from '../../../../core/api/apiDefinitions';

interface KeyAllocation {
  id: number;
  keyId: number;
  keyCode: string;
  handoverTo: string;
  returnedBy: string | null;
  handoverBy: string;
  reason: string | null;
  returnTo: string | null;
  isRestricted:boolean;
  status: string;
  handoverType: string | null;
  handoverDate: string;
  returnDate: string | null;
}

interface DetailedUser {
  empNumber: string;
  name: string;
  email?: string | null;
  status: string;
  phone?: string | null;
  createdAt?: string | null;
  group: string;
  departmentAdminSpecificData?: {
    departmentNames?: string[];
  } | null;
  driverSpecificData?: {
    licenseNumber?: string;
    licenseType?: string;
    expireDate?: string;
  } | null;
}

interface KeyDetailResponse {
  status: string;
  statusDescription: string;
  data: KeyAllocation;
}

interface ViewKeyAllocatedDetailProps {
  show: boolean;
  onHide: () => void;
  keyAllocation: KeyAllocation | null;
  statusColors: { [key: string]: string };
}

interface ViewUserModalProps {
  show: boolean;
  onHide: () => void;
  empNumber: string | null;
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ show, onHide, empNumber }) => {
  const [user, setUser] = useState<DetailedUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!show || !empNumber) return;
      setLoading(true);
      setError(null);
      try {
        const response = await apiDefinitions.getUserByEmpNumber(empNumber);
        if (response.data?.status === 'S1000' && response.data?.data) {
          setUser(response.data.data as DetailedUser);
        } else {
          setError(response.data?.statusDescription || 'Failed to load user details');
          setUser(null);
        }
      } catch (e: any) {
        setError(e?.response?.data?.statusDescription || e?.message || 'Network error');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, empNumber]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return 'N/A';
    const date = new Date(iso);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>User Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading...</span>
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && user && (
          <Table bordered hover>
            <tbody>
              <tr>
                <td><strong>Employee Number</strong></td>
                <td>{user.empNumber || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Name</strong></td>
                <td>{user.name || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{user.email || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Phone</strong></td>
                <td>{user.phone || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Status</strong></td>
                <td>
                  <span
                    className={`badge bg-${
                      user.status === 'INITIAL'
                        ? 'secondary'
                        : user.status === 'PASSWORD_RESET'
                        ? 'primary'
                        : user.status === 'ACTIVE'
                        ? 'success'
                        : 'info'
                    }`}
                  >
                    {user.status || 'N/A'}
                  </span>
                </td>
              </tr>
              <tr>
                <td><strong>User Type</strong></td>
                <td>{user.group?.replace('_K', '') || 'N/A'}</td>
              </tr>
              <tr>
                <td><strong>Department</strong></td>
                <td>
                  {user.departmentAdminSpecificData?.departmentNames?.join(', ') || 'N/A'}
                </td>
              </tr>
              {user.driverSpecificData && (
                <>
                  <tr>
                    <td><strong>License Number</strong></td>
                    <td>{user.driverSpecificData?.licenseNumber || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>License Type</strong></td>
                    <td>{user.driverSpecificData?.licenseType || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>License Expiration Date</strong></td>
                    <td>
                      {user.driverSpecificData?.expireDate
                        ? formatDate(user.driverSpecificData.expireDate)
                        : 'N/A'}
                    </td>
                  </tr>
                </>
              )}
              <tr>
                <td><strong>Created At</strong></td>
                <td>{formatDate(user.createdAt)}</td>
              </tr>
            </tbody>
          </Table>
        )}
        {!loading && !error && !user && (
          <Alert variant="warning">No user details available.</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ViewKeyAllocatedDetail: React.FC<ViewKeyAllocatedDetailProps> = ({
  show,
  onHide,
  keyAllocation,
  statusColors,
}) => {
  const [detailedKey, setDetailedKey] = useState<KeyAllocation | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [selectedEmpNumber, setSelectedEmpNumber] = useState<string | null>(null);

  // Define handover type colors
  const getHandoverTypeClass = (handoverType: string | null) => {
    switch (handoverType) {
      case 'TEMPORARY':
        return 'badge bg-success';
      case 'PERMANENT':
        return 'badge bg-secondary';
      default:
        return 'badge bg-secondary';
    }
  };

  // Format date/time
  const formatDateTime = (date: string | null): string => {
    if (!date) return 'N/A';
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return 'Invalid Date';
    return parsedDate.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Fetch key details
  useEffect(() => {
    const fetchKeyDetails = async (id: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiDefinitions.getHandoverKeyById(id);
        const responseData = response.data as KeyDetailResponse;
        if (responseData.status === 'S1000' && responseData.data) {
          setDetailedKey(responseData.data);
        } else {
          setError(responseData.statusDescription || 'Failed to fetch key details.');
          setDetailedKey(keyAllocation); // Fallback to prop keyAllocation
          Swal.fire({
            icon: 'error',
            title: 'Fetch Error',
            text: responseData.statusDescription || 'Failed to fetch key details.',
          });
        }
      } catch (error: any) {
        const errorMsg = error.response?.data?.statusDescription || 'Failed to fetch key details.';
        setError(errorMsg);
        setDetailedKey(keyAllocation); // Fallback to prop keyAllocation
        Swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: errorMsg,
        });
      } finally {
        setLoading(false);
      }
    };

    if (show && keyAllocation?.id) {
      fetchKeyDetails(keyAllocation.id);
    } else {
      setDetailedKey(keyAllocation);
      if (!keyAllocation) {
        setError('No key allocation data provided.');
        Swal.fire({
          icon: 'warning',
          title: 'No Key Data',
          text: 'No key allocation data provided.',
        });
      }
    }
  }, [show, keyAllocation]);

  const handleViewUserClick = (empNumber: string | null) => {
    if (empNumber) {
      setSelectedEmpNumber(empNumber);
      setShowUserModal(true);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby={`key-allocation-${keyAllocation?.id || 'unknown'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title id={`key-allocation-${keyAllocation?.id || 'unknown'}`}>
            Key Allocation Details - ID: {keyAllocation?.id || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <Alert variant="info">Loading key allocation details...</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && detailedKey && (
            <div className="row">
              <div className="col-md-12">
                <h5>Key Allocation Details</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>ID</strong></td>
                      <td>{detailedKey.id}</td>
                    </tr>
                    <tr>
                      <td><strong>Key ID</strong></td>
                      <td>{detailedKey.keyId}</td>
                    </tr>
                    <tr>
                      <td><strong>Key Code</strong></td>
                      <td>{detailedKey.keyCode}</td>
                    </tr>
                    <tr>
                      <td><strong>Handover To</strong></td>
                      <td>
                        <div className="d-flex align-items-center gap-5">
                          <span>{detailedKey.handoverTo}</span>
                          <button
                            type="button"
                            className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                            onClick={() => handleViewUserClick(detailedKey.handoverTo)}
                            title="View Handover To Details"
                            aria-label="View Handover To Details"
                          >
                            <i className="ti ti-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Handover By</strong></td>
                      <td>
                        <div className="d-flex align-items-center gap-5">
                          <span>{detailedKey.handoverBy}</span>
                          <button
                            type="button"
                            className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                            onClick={() => handleViewUserClick(detailedKey.handoverBy)}
                            title="View Handover By Details"
                            aria-label="View Handover By Details"
                          >
                            <i className="ti ti-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Returned By</strong></td>
                      <td>
                        {detailedKey.returnedBy ? (
                          <div className="d-flex align-items-center gap-5">
                            <span>{detailedKey.returnedBy}</span>
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                              onClick={() => handleViewUserClick(detailedKey.returnedBy)}
                              title="View Returned By Details"
                              aria-label="View Returned By Details"
                            >
                              <i className="ti ti-eye" />
                            </button>
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Return To</strong></td>
                      <td>
                        {detailedKey.returnTo ? (
                          <div className="d-flex align-items-center gap-5">
                            <span>{detailedKey.returnTo}</span>
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                              onClick={() => handleViewUserClick(detailedKey.returnTo)}
                              title="View Return To Details"
                              aria-label="View Return To Details"
                            >
                              <i className="ti ti-eye" />
                            </button>
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Status</strong></td>
                      <td>
                        <Badge bg={statusColors[detailedKey.status] || 'secondary'}>
                          {detailedKey.status || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Handover Type</strong></td>
                      <td>
                        <Badge className={getHandoverTypeClass(detailedKey.handoverType)}>
                          {detailedKey.handoverType || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Is Restricted</strong></td>
                      <td>
                        {detailedKey.isRestricted === null || detailedKey.isRestricted === undefined ? (
                          'N/A'
                        ) : detailedKey.isRestricted ? (
                          <Badge bg="danger">Restricted</Badge>
                        ) : (
                          <Badge bg="success">Not Restricted</Badge>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td><strong>Handover Date</strong></td>
                      <td>{formatDateTime(detailedKey.handoverDate)}</td>
                    </tr>
                    <tr>
                      <td><strong>Return Date</strong></td>
                      <td>{formatDateTime(detailedKey.returnDate)}</td>
                    </tr>
                    <tr>
                      <td><strong>Reason</strong></td>
                      <td>{detailedKey.reason || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          )}
          {!loading && !error && !detailedKey && (
            <Alert variant="warning">No key allocation details available.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ViewUserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        empNumber={selectedEmpNumber}
      />
    </>
  );
};

export default ViewKeyAllocatedDetail;